import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, StatTile, Badge } from "../components/kr";
import { useAuth } from "../lib/auth";
import { Target, TrendUp, Coins, Phone, Repeat, Kanban, ArrowRight, Warning, Clock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip,
  RadialBarChart, RadialBar, PolarAngleAxis, Cell
} from "recharts";

// Compact "OVERDUE SCHEDULES" banner. Sits at the very top of every dashboard
// role and renders nothing when there is no overdue work — so it never adds
// noise on a good day.
const STEP_LABELS = [
  "No batches yet",
  "Batch created",
  "RM logged",
  "Finished goods",
];
function OverdueSchedulesBanner({ overdue }) {
  const [open, setOpen] = React.useState(false);
  if (!overdue || !overdue.count) return null;
  const rows = (overdue.schedules || []).slice(0, 5);

  // Collapsed: a small red pill with a clock icon + count. Click to expand.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-6 inline-flex items-center gap-2 bg-red-50 border border-red-300 hover:border-red-400 hover:bg-red-100 text-red-800 rounded-full pl-2 pr-3 py-1.5 shadow-sm transition-colors"
        data-testid="overdue-schedules-badge"
        title={`${overdue.count} overdue schedule${overdue.count === 1 ? "" : "s"} — click to view`}
      >
        <span className="p-1 bg-red-100 rounded-full text-red-600">
          <Clock size={12} weight="fill" />
        </span>
        <span className="text-xs font-bold">
          {overdue.count} overdue
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-red-500">
          click to view
        </span>
      </button>
    );
  }

  // Expanded: the original detailed banner. Header now has a close button so
  // the operator can collapse it back to the small icon.
  return (
    <div
      className="mb-6 border border-red-300 bg-red-50/70 backdrop-blur rounded-xl p-4 shadow-sm"
      data-testid="overdue-schedules-banner"
    >
      <div className="flex items-start gap-4">
        <div className="p-2 bg-red-100 rounded-lg text-red-600 flex-shrink-0">
          <Clock size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-semibold text-red-900 text-sm tracking-wide">
              {overdue.count} schedule{overdue.count === 1 ? "" : "s"} overdue
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-red-500 bg-white/70 px-1.5 py-0.5 rounded border border-red-200">
              past-dated · step &lt; 3
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto text-[10px] font-mono uppercase tracking-widest text-red-700 hover:text-red-900 bg-white/70 hover:bg-white px-2 py-0.5 rounded border border-red-200"
              data-testid="overdue-schedules-collapse"
            >
              Hide ✕
            </button>
          </div>
          <ul className="mt-2 text-xs space-y-1.5" data-testid="overdue-schedules-list">
            {rows.map(s => (
              <li
                key={s.id}
                className="font-mono bg-white/70 p-2 rounded-lg border border-red-100 flex flex-wrap items-center gap-2"
                data-testid={`overdue-row-${s.id}`}
              >
                <span className="font-semibold text-red-900">{s.product}</span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-700">{s.scheduled_date}</span>
                <span className="text-red-700 font-bold">
                  {s.days_overdue}d late
                </span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-700">
                  Step {s.step}/3 · {STEP_LABELS[s.step]}
                </span>
                <span className="ml-auto text-zinc-500">
                  {s.batches_count} batch{s.batches_count === 1 ? "" : "es"} · target {s.target_qty}{s.unit}
                </span>
              </li>
            ))}
            {overdue.count > rows.length && (
              <li className="text-[11px] font-mono text-red-700 italic px-1">
                + {overdue.count - rows.length} more…
              </li>
            )}
          </ul>
        </div>
        <Link
          to="/production"
          className="text-xs font-bold text-red-700 hover:text-red-900 transition-colors bg-white px-3 py-1.5 rounded-lg border border-red-200 shadow-sm whitespace-nowrap self-start"
          data-testid="overdue-schedules-cta"
        >
          Review <ArrowRight size={10} className="inline ml-1" />
        </Link>
      </div>
    </div>
  );
}

// Twin of OverdueSchedulesBanner but for pending sales-invoice receivables.
// Sits next to the schedules pill so the sales team spots collections at
// shift start. Renders nothing when there are no overdue invoices.
function OverdueInvoicesBanner({ overdue }) {
  const [open, setOpen] = React.useState(false);
  if (!overdue || !overdue.count) return null;
  const rows = (overdue.invoices || []).slice(0, 5);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-6 inline-flex items-center gap-2 bg-orange-50 border border-orange-300 hover:border-orange-400 hover:bg-orange-100 text-orange-800 rounded-full pl-2 pr-3 py-1.5 shadow-sm transition-colors"
        data-testid="overdue-invoices-badge"
        title={`${overdue.count} overdue invoice${overdue.count === 1 ? "" : "s"} — click to view`}
      >
        <span className="p-1 bg-orange-100 rounded-full text-orange-700">
          <Clock size={12} weight="fill" />
        </span>
        <span className="text-xs font-bold">
          {overdue.count} invoice{overdue.count === 1 ? "" : "s"} overdue
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-orange-600">
          click to view
        </span>
      </button>
    );
  }
  return (
    <div
      className="mb-6 border border-orange-300 bg-orange-50/70 backdrop-blur rounded-xl p-4 shadow-sm"
      data-testid="overdue-invoices-banner"
    >
      <div className="flex items-start gap-4">
        <div className="p-2 bg-orange-100 rounded-lg text-orange-700 flex-shrink-0">
          <Clock size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-semibold text-orange-900 text-sm tracking-wide">
              {overdue.count} invoice{overdue.count === 1 ? "" : "s"} overdue for collection
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-orange-600 bg-white/70 px-1.5 py-0.5 rounded border border-orange-200">
              past credit-days · balance &gt; 0
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto text-[10px] font-mono uppercase tracking-widest text-orange-700 hover:text-orange-900 bg-white/70 hover:bg-white px-2 py-0.5 rounded border border-orange-200"
              data-testid="overdue-invoices-collapse"
            >
              Hide ✕
            </button>
          </div>
          <ul className="mt-2 text-xs space-y-1.5" data-testid="overdue-invoices-list">
            {rows.map(inv => (
              <li
                key={inv.doc_id}
                className="font-mono bg-white/70 p-2 rounded-lg border border-orange-100 flex flex-wrap items-center gap-2"
                data-testid={`overdue-invoice-${inv.doc_no}`}
              >
                <span className="font-semibold text-orange-900">{inv.doc_no}</span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-700">{inv.party}</span>
                <span className="text-zinc-500">·</span>
                <span className="text-orange-700 font-bold">{inv.days_overdue}d late</span>
                <span className="ml-auto text-zinc-700">
                  Balance ₹{Number(inv.balance).toLocaleString("en-IN")}
                  {inv.paid > 0 && (
                    <span className="text-[10px] text-zinc-500 ml-1">
                      (paid ₹{Number(inv.paid).toLocaleString("en-IN")})
                    </span>
                  )}
                </span>
              </li>
            ))}
            {overdue.count > rows.length && (
              <li className="text-[11px] font-mono text-orange-700 italic px-1">
                + {overdue.count - rows.length} more…
              </li>
            )}
          </ul>
        </div>
        <Link
          to="/sales-invoices"
          className="text-xs font-bold text-orange-700 hover:text-orange-900 transition-colors bg-white px-3 py-1.5 rounded-lg border border-orange-200 shadow-sm whitespace-nowrap self-start"
          data-testid="overdue-invoices-cta"
        >
          Collect <ArrowRight size={10} className="inline ml-1" />
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [overdue, setOverdue] = useState({ count: 0, schedules: [] });
  const [overdueInv, setOverdueInv] = useState({ count: 0, invoices: [] });

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data));
    api.get("/inventory/low-stock").then(r => {
      setLowStock(r.data);
      if (r.data.length > 0) {
        toast.warning(`${r.data.length} raw material(s) below reorder point`, {
          description: r.data.map(x => `${x.name}: ${x.current}${x.unit} (need ≥ ${x.reorder_point})`).join(" · "),
          duration: 8000,
        });
      }
    }).catch(() => {});
    api.get("/schedules/overdue").then(r => setOverdue(r.data || { count: 0, schedules: [] }))
      .catch(() => {});
    api.get("/documents/overdue-invoices").then(r => setOverdueInv(r.data || { count: 0, invoices: [] }))
      .catch(() => {});
  }, []);

  if (!data) return <div className="p-10 font-sans text-sm text-zinc-400 animate-pulse">Loading workspace...</div>;

  // Item 4 — role-based dashboards
  if (user.role === "accounting") return <AccountingDashboard user={user} data={data} />;
  if (user.role === "production" || user.role === "sales") return <EmployeeDashboard user={user} data={data} overdue={overdue} overdueInv={overdueInv} />;

  const todayPct = data.today_target > 0 ? Math.min(100, (data.today_done / data.today_target) * 100) : 0;
  const goalPct = data.goal_total > 0 ? Math.min(100, (data.goal_done / data.goal_total) * 100) : 0;
  const invChartData = data.inventory.slice(0, 6).map(i => ({ name: i.name, kg: i.total }));

  return (
    <div className="p-6 lg:p-10 bg-zinc-50/50 min-h-screen text-zinc-900 font-sans">
      {/* Header section */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8 pb-5 border-b border-zinc-200">
        <div>
          <div className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase bg-zinc-100 px-2 py-0.5 rounded inline-block mb-2" data-testid="page-label">
            {user.role.toUpperCase()} · CONTROL ROOM
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
            Welcome back, <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-extrabold">{user.name.split(" ")[0]}</span>
          </h1>
        </div>
        <div className="font-mono text-xs text-zinc-500 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-zinc-200/60">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>

      {/* Alerts */}
      <div className="flex flex-wrap items-start gap-2">
        <OverdueSchedulesBanner overdue={overdue} />
        <OverdueInvoicesBanner overdue={overdueInv} />
      </div>
      {lowStock.length > 0 && (
        <div className="mb-6 border border-red-200 bg-red-50/60 backdrop-blur rounded-xl p-4 flex items-start gap-4 shadow-sm" data-testid="low-stock-banner">
          <div className="p-2 bg-red-100 rounded-lg text-red-600 flex-shrink-0">
            <Warning size={20} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-red-900 text-sm tracking-wide">Low Stock Alert</div>
            <div className="text-xs text-red-700/90 mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {lowStock.map(x => (
                <span key={x.raw_material_id} className="font-mono bg-white/60 px-2 py-0.5 rounded border border-red-100">
                  <span className="font-medium text-red-900">{x.name}</span>: {x.current}{x.unit} <span className="text-red-400">/</span> need {x.reorder_point}{x.unit}
                </span>
              ))}
            </div>
          </div>
          <Link to="/inventory" className="text-xs font-bold text-red-700 hover:text-red-900 transition-colors bg-white px-3 py-1.5 rounded-lg border border-red-200 shadow-sm whitespace-nowrap">
            Restock <ArrowRight size={10} className="inline ml-1" />
          </Link>
        </div>
      )}

      {data.credit_alerts?.length > 0 && (
        <div className="mb-6 border border-amber-200 bg-amber-50/60 backdrop-blur rounded-xl p-4 shadow-sm" data-testid="credit-alert-banner">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-amber-100 rounded-md text-amber-600">
              <Warning size={16} weight="fill" />
            </div>
            <div className="font-semibold text-amber-900 text-sm tracking-wide">Credit Cycle Alerts</div>
          </div>
          <ul className="text-xs space-y-2">
            {data.credit_alerts.map(c => (
              <li key={c.doc_id} className="font-mono bg-white/60 p-2.5 rounded-lg border border-amber-100 flex justify-between items-center transition-all hover:bg-white">
                <span className="text-amber-900"><b className="font-semibold">{c.party}</b> — <span className="text-zinc-500">{c.doc_no}</span></span>
                <span className="text-zinc-600 text-right">
                  Due <b className="text-amber-800 font-semibold">{c.when}</b> ({c.due_date}) · <span className="text-zinc-900 font-bold font-sans">₹{c.balance_due?.toLocaleString?.() || c.balance_due}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* KPI Stats Tiles Container */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="dashboard-kpis">
        <StatTile testId="kpi-today-target" label="Today's Target"
          value={`${data.today_target} kg`}
          sub={`${data.today_done} kg done · ${todayPct.toFixed(0)}%`} />
        <StatTile testId="kpi-goal" label="Active Goal" value={`${data.goal_total} kg`}
          sub={`${data.goal_done} kg achieved · ${goalPct.toFixed(0)}%`} accent="text-amber-600" />
        {data.money ? (
          <>
            <StatTile testId="kpi-receivable" label="To Receive"
              value={`₹${data.money.receivable_pending.toLocaleString("en-IN")}`}
              sub="Pending receivables" accent="text-emerald-600" />
            <StatTile testId="kpi-payable" label="To Pay"
              value={`₹${data.money.payable_pending.toLocaleString("en-IN")}`}
              sub="Pending payables" accent="text-rose-600" />
          </>
        ) : (
          <>
            <StatTile testId="kpi-meetings" label="Meetings Today" value={data.meetings_today.length} />
            <StatTile testId="kpi-issues" label="Open Issues" value={data.open_issues_count}
              accent={data.open_issues_count > 0 ? "text-amber-600" : ""} />
          </>
        )}
      </div>

      {/* Charts & Primary Widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Radial progress chart */}
        <Card data-testid="card-progress" className="transition-all hover:shadow-md hover:-translate-y-0.5 border border-zinc-200 bg-white rounded-xl p-5 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
              <Target size={14} className="text-zinc-400" /> Goal Progress
            </div>
            <div className="h-44 relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="78%" outerRadius="100%" data={[{ name: "p", val: goalPct }]} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar dataKey="val" cornerRadius={12} background={{ fill: "#F4F4F5" }}>
                    <Cell fill="url(#amberGradient)" />
                  </RadialBar>
                  {/* Local Defs injection for beautiful modern chart gradients */}
                  <defs>
                    <linearGradient id="amberGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#d97706" />
                      <stop offset="100%" stopColor="#f97316" />
                    </linearGradient>
                  </defs>
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute flex flex-col items-center justify-center pointer-events-none">
                <div className="text-3xl font-black tracking-tight text-zinc-900">{goalPct.toFixed(0)}%</div>
                <div className="text-[10px] font-mono text-zinc-400 mt-0.5">{data.goal_done} / {data.goal_total} kg</div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between">
            <Badge tone={goalPct >= 100 ? "green" : goalPct >= 50 ? "orange" : "red"}>
              {goalPct >= 100 ? "Target met" : goalPct >= 50 ? "On track" : "Behind"}
            </Badge>
          </div>
        </Card>

        {/* Inventory chart */}
        <Card data-testid="card-inv-chart" className="lg:col-span-2 transition-all hover:shadow-md hover:-translate-y-0.5 border border-zinc-200 bg-white rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase flex items-center gap-2">
              <TrendUp size={14} className="text-zinc-400" /> Stock Levels
            </div>
            <Link to="/inventory" className="text-xs font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1 transition-colors">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {invChartData.length === 0 ? (
            <div className="h-44 flex items-center justify-center text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-xl bg-zinc-50/50">
              No raw materials in stock
            </div>
          ) : (
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={invChartData} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#ea580c" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="name" stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} dy={8} />
                  <YAxis stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} dx={-4} />
                  <Tooltip 
                    cursor={{ fill: "rgba(244,244,245,0.6)", radius: 4 }}
                    contentStyle={{ backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #E4E4E7", fontSize: 12, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }} 
                  />
                  <Bar dataKey="kg" fill="url(#barGradient)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Secondary Dashboard Content Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Renewals */}
        <Card data-testid="card-renewals" className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase flex items-center gap-2">
                <Repeat size={14} className="text-zinc-400" /> Upcoming Renewals
              </div>
              <Link to="/subscriptions" className="text-zinc-400 hover:text-zinc-600 transition-colors">
                <ArrowRight size={14} />
              </Link>
            </div>
            {data.upcoming_renewals?.length === 0 && (
              <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-4 text-center bg-zinc-50/50">
                No upcoming renewals.
              </p>
            )}
            <ul className="space-y-2.5">
              {data.upcoming_renewals?.map(s => (
                <li key={s.id} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 transition-colors hover:bg-zinc-100/70">
                  <div className="font-semibold text-sm text-zinc-800">{s.name}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1 flex justify-between">
                    <span>{s.next_renewal}</span>
                    <span className="font-sans font-bold text-zinc-700">₹{s.amount.toLocaleString("en-IN")}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        {/* Meetings */}
        <Card data-testid="card-meetings" className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5">
          <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
            <Phone size={14} className="text-zinc-400" /> Today's Meetings
          </div>
          {data.meetings_today.length === 0 && (
            <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-4 text-center bg-zinc-50/50">
              Nothing scheduled today.
            </p>
          )}
          <ul className="space-y-2.5">
            {data.meetings_today.map((m) => (
              <li key={m.id} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 transition-colors hover:bg-zinc-100/70">
                <div className="font-semibold text-sm text-zinc-800">{m.title}</div>
                <div className="text-xs text-zinc-500 font-mono mt-1 flex items-center justify-between">
                  <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                    {new Date(m.datetime_iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  {m.with_party && <span className="truncate max-w-[60%] text-zinc-400">{m.with_party}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {/* Open issues */}
        <Card data-testid="card-issues" className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase flex items-center gap-2">
                <Kanban size={14} className="text-zinc-400" /> Open Issues
              </div>
              <Link to="/issues" className="text-zinc-400 hover:text-zinc-600 transition-colors">
                <ArrowRight size={14} />
              </Link>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-5xl font-black tracking-tight text-zinc-900">{data.open_issues_count}</span>
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Active</span>
            </div>
            <p className="text-xs text-zinc-400 mt-2">Tasks & system improvements currently pending execution.</p>
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-100">
            <Link to="/issues" className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 transition-colors flex items-center justify-between">
              <span>View full backlog</span>
              <ArrowRight size={12} />
            </Link>
          </div>
        </Card>

        {/* Cash Position Banner block */}
        {data.money && (
          <Card className="lg:col-span-3 transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-6" data-testid="card-money">
            <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
              <Coins size={14} className="text-zinc-400" /> Cash Position Summary
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
                <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">Received</div>
                <div className="font-mono text-lg font-bold text-emerald-600 mt-1">₹{data.money.received.toLocaleString("en-IN")}</div>
              </div>
              <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
                <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">Paid</div>
                <div className="font-mono text-lg font-bold text-rose-600 mt-1">₹{data.money.paid.toLocaleString("en-IN")}</div>
              </div>
              <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
                <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">To Receive</div>
                <div className="font-mono text-lg font-bold text-zinc-800 mt-1">₹{data.money.receivable_pending.toLocaleString("en-IN")}</div>
              </div>
              <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
                <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">To Pay</div>
                <div className="font-mono text-lg font-bold text-zinc-800 mt-1">₹{data.money.payable_pending.toLocaleString("en-IN")}</div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ----------------- Accounting-role dashboard (item 4) -----------------
function AccountingDashboard({ user, data }) {
  const [sheet, setSheet] = React.useState(null);
  const [expenses, setExpenses] = React.useState([]);
  React.useEffect(() => {
    api.get("/expense-sheet").then(r => setSheet(r.data)).catch(() => {});
    api.get("/misc-purchases").then(r => setExpenses(r.data.slice(0, 8))).catch(() => {});
  }, []);
  const t = sheet?.totals;
  return (
    <div className="p-6 lg:p-10 bg-zinc-50/50 min-h-screen text-zinc-900 font-sans">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8 pb-5 border-b border-zinc-200">
        <div>
          <div className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase bg-zinc-100 px-2 py-0.5 rounded inline-block mb-2">
            {user.role.toUpperCase()} · SUPPLIES & OUTGOINGS
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
            Hello, <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-extrabold">{user.name.split(" ")[0]}</span>
          </h1>
        </div>
      </div>

      {data.money && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatTile testId="acc-dash-payable" label="To Pay" value={`₹${data.money.payable_pending.toLocaleString("en-IN")}`} accent="text-rose-600" sub="Outstanding payables" />
          <StatTile testId="acc-dash-paid" label="Paid" value={`₹${data.money.paid.toLocaleString("en-IN")}`} accent="text-zinc-700" />
          <StatTile testId="acc-dash-receivable" label="To Receive" value={`₹${data.money.receivable_pending.toLocaleString("en-IN")}`} accent="text-emerald-600" />
          <StatTile testId="acc-dash-received" label="Received" value={`₹${data.money.received.toLocaleString("en-IN")}`} />
        </div>
      )}

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatTile label="Salaries / month" value={`₹${t.salaries_monthly.toLocaleString("en-IN")}`} sub="Active employees" />
          <StatTile label="Subscriptions / month" value={`₹${t.subscriptions_monthly.toLocaleString("en-IN")}`} />
          <StatTile label="Fixed costs / month" value={`₹${t.fixed_monthly.toLocaleString("en-IN")}`} />
          <StatTile label="Total monthly burn"
            value={`₹${(t.salaries_monthly + t.subscriptions_monthly + t.fixed_monthly).toLocaleString("en-IN")}`}
            accent="text-rose-600" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5 flex flex-col justify-between">
          <div>
            <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
              <Coins size={14} className="text-zinc-400" /> Recent Expense Requests
            </div>
            {expenses.length === 0 ? (
              <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-4 text-center bg-zinc-50/50">No expense requests yet.</p>
            ) : (
              <ul className="space-y-2">
                {expenses.map(m => (
                  <li key={m.id} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 transition-colors hover:bg-zinc-100/70 flex justify-between items-center text-sm">
                    <div>
                      <div className="font-semibold text-zinc-800">{m.vendor}</div>
                      <div className="text-[11px] text-zinc-400 font-mono mt-0.5">{m.date} · {m.category} · <span className="text-amber-600 font-medium">{m.status || "approved"}</span></div>
                    </div>
                    <div className="font-mono font-bold text-zinc-900">₹{m.amount.toLocaleString("en-IN")}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-100">
            <Link to="/misc-purchases" className="text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors">
              View all requests →
            </Link>
          </div>
        </Card>

        <Card className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5">
          <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
            <Repeat size={14} className="text-zinc-400" /> Upcoming Renewals
          </div>
          {(data.upcoming_renewals || []).length === 0 && <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-4 text-center bg-zinc-50/50">No upcoming renewals.</p>}
          <ul className="space-y-2.5">
            {(data.upcoming_renewals || []).map(s => (
              <li key={s.id} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 transition-colors hover:bg-zinc-100/70">
                <div className="font-semibold text-sm text-zinc-800">{s.name}</div>
                <div className="text-xs font-mono text-zinc-500 mt-1 flex justify-between">
                  <span>{s.next_renewal}</span>
                  <span className="font-sans font-bold text-zinc-700">₹{s.amount.toLocaleString("en-IN")}</span>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

// ----------------- Employee (production/sales) dashboard (item 4) -----------------
function EmployeeDashboard({ user, data, overdue, overdueInv }) {
  const [my, setMy] = React.useState(null);
  const [balance, setBalance] = React.useState(null);
  React.useEffect(() => {
    api.get("/employees").then(async (r) => {
      const emp = r.data.find(e => e.name === user.name);
      if (!emp) return;
      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      try {
        const [pay, bal] = await Promise.all([
          api.get(`/payroll/${emp.id}/${year}/${month}`),
          api.get(`/employees/${emp.id}/leave-summary/${year}`),
        ]);
        setMy({ emp, pay: pay.data });
        setBalance(bal.data);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("Employee dashboard fetch failed:", e?.message);
      }
    });
  }, [user.name]);

  return (
    <div className="p-6 lg:p-10 bg-zinc-50/50 min-h-screen text-zinc-900 font-sans">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8 pb-5 border-b border-zinc-200">
        <div>
          <div className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase bg-zinc-100 px-2 py-0.5 rounded inline-block mb-2">
            {user.role.toUpperCase()} · MY DASHBOARD
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
            Hello, <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-extrabold">{user.name.split(" ")[0]}</span>
          </h1>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-2">
        <OverdueSchedulesBanner overdue={overdue} />
        <OverdueInvoicesBanner overdue={overdueInv} />
      </div>

      {balance && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatTile testId="emp-quota" label="Annual Quota" value={balance.annual_leave_quota} sub={`${balance.year}`} />
          <StatTile testId="emp-used" label="Leaves Used" value={balance.used_days} accent="text-amber-600" />
          <StatTile testId="emp-pending" label="Pending Requests" value={balance.pending_days} sub="Awaiting approval" />
          <StatTile testId="emp-remaining" label="Remaining" value={balance.remaining}
            accent={balance.remaining <= 0 ? "text-rose-600" : "text-emerald-600"}
            sub={balance.remaining <= 0 ? "No balance" : "Available to take"} />
        </div>
      )}

      {my?.pay && (
        <Card className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5 mb-6">
          <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
            <Coins size={14} className="text-zinc-400" /> This Month's Payroll (Estimated)
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
              <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">Present days</div>
              <div className="font-mono text-xl font-bold text-zinc-800 mt-1">{my.pay.counts?.present || 0}</div>
            </div>
            <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
              <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">Special holiday</div>
              <div className="font-mono text-xl font-bold text-zinc-800 mt-1">{my.pay.counts?.special_holiday || 0}</div>
            </div>
            <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
              <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">Unpaid leaves</div>
              <div className="font-mono text-xl font-bold text-rose-600 mt-1">{my.pay.unpaid_leaves || 0}</div>
            </div>
            <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
              <div className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">Estimated payable</div>
              <div className="font-mono text-xl font-bold text-emerald-600 mt-1">₹{(my.pay.net_payable || my.pay.payable || 0).toLocaleString("en-IN")}</div>
              {my.pay.deductions > 0 && (
                <div className="text-[10px] text-rose-600 font-mono mt-0.5">− ₹{my.pay.deductions.toLocaleString("en-IN")} deducted</div>
              )}
            </div>
          </div>
        </Card>
      )}

      {!my && (
        <Card className="border border-dashed border-zinc-200 bg-zinc-50/50 rounded-xl p-5 mb-6">
          <p className="text-xs text-zinc-500 text-center">
            Your payroll and leave summary become visible once an admin adds you as an employee (matched by name).
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5">
          <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
            <Phone size={14} className="text-zinc-400" /> Today's Meetings
          </div>
          {data.meetings_today.length === 0 && <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-4 text-center bg-zinc-50/50">Nothing scheduled today.</p>}
          <ul className="space-y-2.5">
            {data.meetings_today.map((m) => (
              <li key={m.id} className="bg-zinc-50 border border-zinc-100 rounded-lg p-3 transition-colors hover:bg-zinc-100/70">
                <div className="font-semibold text-sm text-zinc-800">{m.title}</div>
                <div className="text-xs text-zinc-500 font-mono mt-1 flex items-center justify-between">
                  <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                    {new Date(m.datetime_iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  {m.with_party && <span className="truncate max-w-[60%] text-zinc-400">{m.with_party}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="transition-all hover:shadow-md border border-zinc-200 bg-white rounded-xl p-5 flex flex-col justify-between">
          <div>
            <div className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-4 flex items-center gap-2">
              <Kanban size={14} className="text-zinc-400" /> Open Issues
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-5xl font-black tracking-tight text-zinc-900">{data.open_issues_count}</span>
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Assigned Tasks</span>
            </div>
            <p className="text-xs text-zinc-400 mt-2">Tasks & system improvements currently assigned to the team.</p>
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-100">
            <Link to="/issues" className="text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors flex items-center gap-1">
              Open board <ArrowRight size={12} />
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}